import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
	useParams,
} from "react-router-dom";
import { AuthRoutes } from "../../features/auth/presentation/routes/AuthRoutes";
import { useEffect, useMemo, useState } from "react";
import { onRenew } from "../../features/auth/presentation/store/auth/AuthThunk";
import CircularProgress from "../components/CircularProgress";
import ProfileRoutes from "../../features/profile/presentation/routes/ProfileRoutes";
import { getAffiliateLoginPath } from "../../features/auth/presentation/routes/affiliateRedirect";
import { AffiliateOnlyDashboardScreen } from "../../features/settings/presentation/pages/AffiliateOnlyDashboardScreen";
import { AffiliateScreen } from "../../features/settings/presentation/pages/AffiliateScreen";
import { apiServices, setToken } from "../utilities/ApiServices";
import { LikeEmailSettingsScreen } from "../../features/settings/presentation/pages/LikeEmailSettingsScreen";
import { AccountRecoveryScreen } from "../../features/profile/presentation/pages/recovery/AccountRecoveryScreen";
import { saveAccountRecoveryIntent } from "../utilities/accountRecovery";

const consumeAffiliateAppToken = () => {
	if (typeof window === "undefined") return false;

	const url = new URL(window.location.href);
	const path = url.pathname;
	const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
	const appToken =
		url.searchParams.get("appToken")?.trim() ||
		hashParams.get("appToken")?.trim();
	const isMemberAffiliatePath =
		path === "/affiliate" ||
		path.startsWith("/affiliate/") ||
		path === "/affiliate-app" ||
		path.startsWith("/affiliate-app/") ||
		path === "/settings/affiliate" ||
		path.startsWith("/settings/affiliate/");

	if (!appToken || !isMemberAffiliatePath) return false;

	window.localStorage.setItem("userToken", appToken);
	setToken(appToken);
	url.searchParams.delete("appToken");
	hashParams.delete("appToken");
	url.hash = hashParams.toString() ? `#${hashParams.toString()}` : "";
	window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
	return true;
};

const AffiliateReferralRedirect = () => {
	const { affiliateCode } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		let cancelled = false;
		const resolveReferral = async () => {
			const rawCode = affiliateCode || "";
			let targetCode = rawCode;
			try {
				const response = await apiServices.get(
					"/affiliate/public/resolve-code",
					{
						params: { code: rawCode },
					},
				);
				if (response.data?.found && response.data?.code) {
					targetCode = response.data.code;
				}
			} catch {
				targetCode = rawCode;
			}
			if (!cancelled) {
				navigate(`/auth/login?referCode=${encodeURIComponent(targetCode)}`, {
					replace: true,
				});
			}
		};

		resolveReferral();
		return () => {
			cancelled = true;
		};
	}, [affiliateCode, navigate]);

	return (
		<div className="h-svh flex items-center justify-center">
			<CircularProgress />
		</div>
	);
};

const AccountRecoveryHandoff = ({ signedIn }: { signedIn: boolean }) => {
	const navigate = useNavigate();

	useEffect(() => {
		saveAccountRecoveryIntent();
		navigate(signedIn ? "/recover-profile" : "/auth/login", { replace: true });
	}, [navigate, signedIn]);

	return (
		<div className="h-svh flex items-center justify-center">
			<CircularProgress />
		</div>
	);
};

export const AppRouter = () => {
	const dispatch: AppDispatch = useDispatch();
	const routerLocation = useLocation();
	const isPublicLikeEmailSettings = routerLocation.pathname === "/like-email-settings";
	const { isRenewLoading, login } = useSelector(
		(state: RootState) => state.auth,
	);
	const savedLoginState = useMemo(() => {
		const usedAffiliateAppToken = consumeAffiliateAppToken();
		if (typeof window === "undefined") {
			return { hasStoredToken: false, skipRenew: false };
		}
		return {
			hasStoredToken: Boolean(window.localStorage.getItem("userToken")),
			skipRenew:
				isPublicLikeEmailSettings ||
				(usedAffiliateAppToken &&
					window.location.pathname.startsWith("/affiliate-app")),
		};
	}, [isPublicLikeEmailSettings]);
	const [hasCheckedSavedLogin, setHasCheckedSavedLogin] =
		useState(!savedLoginState.hasStoredToken || savedLoginState.skipRenew);

	useEffect(() => {
		if (!savedLoginState.hasStoredToken || savedLoginState.skipRenew) {
			setHasCheckedSavedLogin(true);
			return;
		}

		dispatch(onRenew()).finally(() => {
			setHasCheckedSavedLogin(true);
		});
	}, [dispatch, savedLoginState.hasStoredToken, savedLoginState.skipRenew]);

	if (isRenewLoading || !hasCheckedSavedLogin) {
		return (
			<div className="h-svh flex items-center justify-center">
				<CircularProgress />
			</div>
		);
	}
	return (
		<Routes>
			<Route path="/like-email-settings" element={<LikeEmailSettingsScreen />} />
			<Route path="/recover-profile" element={<AccountRecoveryScreen />} />
			<Route path="/affiliate-app" element={<AffiliateScreen />} />
			<Route path="/affiliate-app/:section" element={<AffiliateScreen />} />
			{login === false ? (
				<>
					<Route path="/open" element={<AccountRecoveryHandoff signedIn={false} />} />
					<Route path="/auth/*" element={<AuthRoutes />} />
					<Route
						path="/r/:affiliateCode"
						element={<AffiliateReferralRedirect />}
					/>
					<Route
						path="/affiliate-dashboard"
						element={<AffiliateOnlyDashboardScreen />}
					/>
					<Route
						path="/affiliate-dashboard/:section"
						element={<AffiliateOnlyDashboardScreen />}
					/>
					<Route
						path="/affiliate"
						element={<Navigate to={getAffiliateLoginPath()} replace />}
					/>
					<Route
						path="/settings/affiliate"
						element={<Navigate to={getAffiliateLoginPath()} replace />}
					/>
				</>
			) : (
				<>
					<Route
						path="/open"
						element={<AccountRecoveryHandoff signedIn />}
					/>
					<Route
						path="/affiliate-dashboard"
						element={<AffiliateOnlyDashboardScreen />}
					/>
					<Route
						path="/affiliate-dashboard/:section"
						element={<AffiliateOnlyDashboardScreen />}
					/>
					<Route
						path="/r/:affiliateCode"
						element={<AffiliateReferralRedirect />}
					/>
					<Route path="/*" element={<ProfileRoutes />} />
				</>
			)}

			<Route path="/*" element={<Navigate to="/auth/login" />} />
		</Routes>
	);
};
